import type { ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3,
  BookMarked,
  BookOpen,
  CheckSquare,
  History,
  Home,
  LogOut,
  Users,
  type LucideIcon,
} from "lucide-react";
import { logout as logoutRequest } from "../api/auth";
import { fileUrl } from "../api/files";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { ROLE_LABELS, type AcademyRole } from "../types/user";
import { NotificationBell } from "./NotificationBell";

const STAFF_ROLES: AcademyRole[] = [
  "super_admin",
  "admin",
  "content_manager",
  "curator",
  "mentor",
];

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  visible: boolean;
}

interface Props {
  children: ReactNode;
}

/**
 * Layout-обёртка с условным sidebar для staff на md+ ширинах.
 *
 * - Trainee/guide/manager — всегда mobile-first без sidebar (как было)
 * - Editors/curators/admins на десктопе видят sidebar; на мобильном —
 *   тот же mobile-first layout с навигацией через плитки на Dashboard
 */
export function AppShell({ children }: Props) {
  const { user, setUser } = useCurrentUser();
  const location = useLocation();
  const navigate = useNavigate();

  if (!user) return <>{children}</>;

  const isStaff = user.roles.some((r) => STAFF_ROLES.includes(r));

  const canSeeUsers = user.roles.some((r) =>
    (["super_admin", "admin", "content_manager", "curator", "mentor"] as AcademyRole[]).includes(r),
  );
  const canSeeAnalytics = user.roles.some((r) =>
    (["super_admin", "admin", "curator", "mentor", "content_manager"] as AcademyRole[]).includes(r),
  );
  const canSeeAudit = user.roles.some((r) =>
    (["super_admin", "admin"] as AcademyRole[]).includes(r),
  );

  const items: NavItem[] = [
    { to: "/", label: "Главная", icon: Home, visible: true },
    { to: "/courses", label: "Курсы", icon: BookOpen, visible: true },
    { to: "/quizzes", label: "Тесты", icon: CheckSquare, visible: true },
    { to: "/kb", label: "База знаний", icon: BookMarked, visible: true },
    { to: "/users", label: "Пользователи", icon: Users, visible: canSeeUsers },
    { to: "/analytics", label: "Аналитика", icon: BarChart3, visible: canSeeAnalytics },
    { to: "/audit", label: "Журнал", icon: History, visible: canSeeAudit },
  ];

  async function handleLogout() {
    await logoutRequest();
    setUser(null);
    navigate("/login", { replace: true });
  }

  // Non-staff or login/change-password pages → render bare content
  if (!isStaff || location.pathname === "/login" || location.pathname === "/change-password") {
    return <>{children}</>;
  }

  return (
    <div className="flex min-h-full">
      {/* Sidebar — visible only on md+ */}
      <aside className="hidden w-60 shrink-0 flex-col bg-brand text-white md:flex">
        <div className="border-b border-white/10 px-5 py-5">
          <Link to="/" className="block">
            <div className="text-base font-bold tracking-tight">Петрополь</div>
            <div className="text-xs uppercase tracking-widest text-white/60">
              Академия
            </div>
          </Link>
        </div>

        <nav className="flex-1 space-y-0.5 px-2 py-3">
          {items
            .filter((i) => i.visible)
            .map((i) => {
              const active =
                i.to === "/"
                  ? location.pathname === "/"
                  : location.pathname.startsWith(i.to);
              const Icon = i.icon;
              return (
                <Link
                  key={i.to}
                  to={i.to}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition ${
                    active
                      ? "bg-white/10 text-white"
                      : "text-white/75 hover:bg-white/5 hover:text-white"
                  }`}
                >
                  <Icon size={16} strokeWidth={2} />
                  {i.label}
                </Link>
              );
            })}
        </nav>

        {/* User card at bottom */}
        <div className="border-t border-white/10 px-3 py-3">
          <div className="flex items-center gap-2 rounded-lg px-2 py-1.5">
            {user.avatar_file_id ? (
              <img
                src={fileUrl(user.avatar_file_id)}
                alt=""
                className="h-8 w-8 shrink-0 rounded-full object-cover ring-2 ring-white/20"
              />
            ) : (
              <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-white/15 text-xs font-semibold uppercase">
                {user.full_name.charAt(0)}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate text-xs font-medium text-white">
                {user.full_name}
              </div>
              <div className="truncate text-[11px] text-white/55">
                {ROLE_LABELS[user.roles[0] ?? "trainee"] ?? user.roles[0]}
              </div>
            </div>
            <NotificationBell tone="dark" />
            <button
              type="button"
              onClick={handleLogout}
              className="grid h-7 w-7 shrink-0 place-items-center rounded text-white/60 transition hover:bg-white/10 hover:text-white"
              title="Выйти"
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="min-w-0 flex-1">{children}</main>
    </div>
  );
}
